import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Columns3, ListChecks, Plus, Trash2, ChevronUp, ChevronDown, FileStack } from "lucide-react"
import {
 Card,
 CardContent,
 CardHeader,
 CardTitle,
 ModernButton,
 ModernInput,
 Tabs,
 TabsContent,
 TabsList,
 TabsTrigger,
 Toast,
 ToastType,
 Checkbox,
 Label,
} from "../../components/ui"
import { cn } from "@/lib/utils"
import { Switch } from "../../components/ui"
import { apiService } from "../../services/api"
import type {
 SaleContractTemplate,
 SaleCustomerDocumentType,
 SaleOperationalKanbanColumn,
 SaleOperationalStage,
} from "../../types"

const CONTRACT_REFERENCE_ACCEPT =
 "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,image/png,image/webp"

const NEW_CONTRACT_UPLOAD_KEY = "__new__"
import ConfirmDialog from "../../components/ui/ConfirmDialog"

type ChecklistTpl = {
 id: string
 name: string
 operationalStage: string
 isActive: boolean
 isDefault: boolean
 items: Array<{ id: string; title: string; sortOrder: number; required: boolean }>
}

type PreparationAssignee = {
 id: string
 name: string
 email: string
}

const KANBAN_STAGES: SaleOperationalStage[] = ["preparation", "post_receiving"]
const CHECKLIST_STAGES: SaleOperationalStage[] = [
  "pre_sale",
  "preparation",
  "delivery",
  "post_receiving",
]

export const OperationPipelineSettingsPage: React.FC = () => {
 const { t } = useTranslation()
 const [tab, setTab] = useState<"kanban" | "checklists" | "saleOnboarding">("saleOnboarding")
 const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)

 const [kanbanStage, setKanbanStage] = useState<SaleOperationalStage>("preparation")
 const [columns, setColumns] = useState<SaleOperationalKanbanColumn[]>([])
 const [newColTitle, setNewColTitle] = useState("")
 const [columnToDelete, setColumnToDelete] = useState<SaleOperationalKanbanColumn | null>(null)
 const [columnChecklistDrafts, setColumnChecklistDrafts] = useState<Record<string, string[]>>({})
 const [columnResponsibleDrafts, setColumnResponsibleDrafts] = useState<Record<string, string>>({})
 const [checklistSavingId, setChecklistSavingId] = useState<string | null>(null)
 const [preparationAssignees, setPreparationAssignees] = useState<PreparationAssignee[]>([])

 const [checklistStageFilter, setChecklistStageFilter] = useState<string>("")
 const [templates, setTemplates] = useState<ChecklistTpl[]>([])
 const [tplName, setTplName] = useState("")
 const [tplStage, setTplStage] = useState<SaleOperationalStage>("pre_sale")
 const [tplItemLines, setTplItemLines] = useState<string[]>([""])
 const [expandedTplId, setExpandedTplId] = useState<string | null>(null)
 const [tplToDelete, setTplToDelete] = useState<ChecklistTpl | null>(null)

 const [docTypes, setDocTypes] = useState<SaleCustomerDocumentType[]>([])
 const [contractTemplates, setContractTemplates] = useState<SaleContractTemplate[]>([])
 const [newDocName, setNewDocName] = useState("")
 const [newDocSlug, setNewDocSlug] = useState("")
 const [newDocAttach, setNewDocAttach] = useState<"sale" | "customer">("sale")
 const [newDocSort, setNewDocSort] = useState(0)
 const [newTplName, setNewTplName] = useState("")
 const [newTplUrl, setNewTplUrl] = useState("")
 const [newTplTempFilename, setNewTplTempFilename] = useState("")
 const [newTplRefLabel, setNewTplRefLabel] = useState("")
 const [newTplSort, setNewTplSort] = useState(0)
 const [contractRefUploadingId, setContractRefUploadingId] = useState<string | null>(null)
 const [docTypeToDelete, setDocTypeToDelete] = useState<SaleCustomerDocumentType | null>(null)
 const [contractTplToDelete, setContractTplToDelete] = useState<SaleContractTemplate | null>(null)

 const loadKanban = useCallback(async () => {
 try {
 const data = await apiService.getSaleKanbanColumns(kanbanStage)
 const nextColumns = Array.isArray(data) ? data : []
 setColumns(nextColumns)
 setColumnResponsibleDrafts(
  Object.fromEntries(nextColumns.map((column) => [column.id, column.responsibleUserId ?? ""]))
 )
 } catch {
 setToast({ message: t("settings.operationPipeline.kanbanLoadError"), type: "error" })
 setColumns([])
 setColumnResponsibleDrafts({})
 }
 }, [kanbanStage, t])

 const loadPreparationAssignees = useCallback(async () => {
  try {
   const raw = await apiService.getPreparationAssignees()
   const normalized = Array.isArray(raw)
    ? raw.filter((user) => user?.id && user?.name).map((user) => ({
       id: user.id,
       name: user.name,
       email: user.email,
      }))
    : []
   setPreparationAssignees(normalized)
  } catch {
   setPreparationAssignees([])
  }
 }, [])

 const loadTemplates = useCallback(async () => {
 try {
 const data = await apiService.getSaleChecklistTemplates(
 checklistStageFilter ? { operationalStage: checklistStageFilter } : undefined
 )
 setTemplates(Array.isArray(data) ? data : [])
 } catch {
 setToast({ message: t("settings.operationPipeline.checklistLoadError"), type: "error" })
 setTemplates([])
 }
 }, [checklistStageFilter, t])

 const loadSaleOnboarding = useCallback(async () => {
 try {
 const [dt, ct] = await Promise.all([
 apiService.getSaleCustomerDocumentTypes(),
 apiService.getSaleContractTemplates(),
 ])
 setDocTypes(Array.isArray(dt) ? dt : [])
 setContractTemplates(Array.isArray(ct) ? ct : [])
 } catch {
 setToast({ message: t("settings.operationPipeline.onboardingLoadError"), type: "error" })
 setDocTypes([])
 setContractTemplates([])
 }
 }, [t])

 useEffect(() => {
 void loadKanban()
 }, [loadKanban])

 useEffect(() => {
  if (kanbanStage !== "preparation") {
   setPreparationAssignees([])
   return
  }
  void loadPreparationAssignees()
 }, [kanbanStage, loadPreparationAssignees])

 useEffect(() => {
  const mids = columns.slice(1, -1)
  if (mids.length === 0) {
   setColumnChecklistDrafts({})
   return
  }
  let cancelled = false
  void Promise.all(
   mids.map(async (c) => {
    try {
     const res = await apiService.getKanbanColumnChecklist(c.id)
     const titles = (res.items ?? []).map((i) => i.title)
     return [c.id, titles.length > 0 ? titles : [""]] as const
    } catch {
     return [c.id, [""]] as const
    }
   })
  ).then((pairs) => {
   if (!cancelled) {
    setColumnChecklistDrafts(Object.fromEntries(pairs))
   }
  })
  return () => {
   cancelled = true
  }
 }, [columns])

 useEffect(() => {
 void loadTemplates()
 }, [loadTemplates])

 const persistReorder = async (next: SaleOperationalKanbanColumn[]) => {
 const orderedColumnIds = next.map((c) => c.id)
 try {
 const data = await apiService.reorderSaleKanbanColumns(kanbanStage, orderedColumnIds)
 setColumns(Array.isArray(data) ? data : next)
 setToast({ message: t("settings.operationPipeline.reorderOk"), type: "success" })
 } catch {
 setToast({ message: t("settings.operationPipeline.reorderError"), type: "error" })
 void loadKanban()
 }
 }

 const moveColumn = async (idx: number, dir: -1 | 1) => {
 const j = idx + dir
 if (j < 0 || j >= columns.length) return
 const next = [...columns]
 const a = next[idx]
 const b = next[j]
 if (!a || !b) return
 next[idx] = b
 next[j] = a
 await persistReorder(next)
 }

 const addColumn = async () => {
 const title = newColTitle.trim()
 if (!title) return
 try {
 await apiService.createSaleKanbanColumn({
 operationalStage: kanbanStage,
 title,
 })
 setNewColTitle("")
 setToast({ message: t("settings.operationPipeline.columnCreated"), type: "success" })
 void loadKanban()
 } catch {
 setToast({ message: t("settings.operationPipeline.columnSaveError"), type: "error" })
 }
 }

 const patchColumn = async (
 columnId: string,
 patch: { title?: string; isActive?: boolean; isHandoffColumn?: boolean; responsibleUserId?: string | null }
 ) => {
 try {
 await apiService.updateSaleKanbanColumn(columnId, patch)
 void loadKanban()
 } catch {
 setToast({ message: t("settings.operationPipeline.columnSaveError"), type: "error" })
 }
 }

 const updateColumnChecklistLine = (columnId: string, index: number, value: string) => {
  setColumnChecklistDrafts((prev) => {
   const arr = [...(prev[columnId] ?? [""])]
   arr[index] = value
   return { ...prev, [columnId]: arr }
  })
 }

 const addColumnChecklistLine = (columnId: string) => {
  setColumnChecklistDrafts((prev) => {
   const arr = [...(prev[columnId] ?? [])]
   arr.push("")
   return { ...prev, [columnId]: arr }
  })
 }

 const removeColumnChecklistLine = (columnId: string, index: number) => {
  setColumnChecklistDrafts((prev) => {
   const arr = [...(prev[columnId] ?? [])]
   arr.splice(index, 1)
   const next = arr.length > 0 ? arr : [""]
   return { ...prev, [columnId]: next }
  })
 }

 const saveColumnChecklistLines = async (columnId: string) => {
 const lines = (columnChecklistDrafts[columnId] ?? []).map((s) => s.trim()).filter(Boolean)
 try {
 setChecklistSavingId(columnId)
 await apiService.putKanbanColumnChecklist(
 columnId,
 lines.map((title, i) => ({ title, sortOrder: i, required: true }))
 )
 setToast({ message: t("settings.operationPipeline.columnChecklistSaved"), type: "success" })
 } catch {
 setToast({ message: t("settings.operationPipeline.columnChecklistError"), type: "error" })
 } finally {
 setChecklistSavingId(null)
 }
 }

 const confirmDeleteColumn = async () => {
 if (!columnToDelete) return
 try {
 await apiService.deleteSaleKanbanColumn(columnToDelete.id)
 setColumnToDelete(null)
 setToast({ message: t("settings.operationPipeline.columnDeleted"), type: "success" })
 void loadKanban()
 } catch {
 setToast({ message: t("settings.operationPipeline.columnDeleteError"), type: "error" })
 }
 }

 const updateNewTplLine = (index: number, value: string) => {
  setTplItemLines((prev) => {
   const arr = [...prev]
   arr[index] = value
   return arr
  })
 }

 const addNewTplLine = () => {
  setTplItemLines((prev) => [...prev, ""])
 }

 const removeNewTplLine = (index: number) => {
  setTplItemLines((prev) => {
   const arr = [...prev]
   arr.splice(index, 1)
   return arr.length > 0 ? arr : [""]
  })
 }

 const createTemplate = async () => {
 const name = tplName.trim()
 const lines = tplItemLines.map((s) => s.trim()).filter(Boolean)
 if (!name || lines.length === 0) {
 setToast({ message: t("settings.operationPipeline.templateFormInvalid"), type: "error" })
 return
 }
 try {
 await apiService.createSaleChecklistTemplate({
 operationalStage: tplStage,
 name,
 isActive: true,
 isDefault: false,
 items: lines.map((title, idx) => ({ title, sortOrder: idx, required: true })),
 })
 setTplName("")
 setTplItemLines([""])
 setToast({ message: t("settings.operationPipeline.templateCreated"), type: "success" })
 void loadTemplates()
 } catch {
 setToast({ message: t("settings.operationPipeline.templateSaveError"), type: "error" })
 }
 }

 const confirmDeleteTemplate = async () => {
 if (!tplToDelete) return
 try {
 await apiService.deleteSaleChecklistTemplate(tplToDelete.id)
 setTplToDelete(null)
 setToast({ message: t("settings.operationPipeline.templateDeleted"), type: "success" })
 void loadTemplates()
 } catch {
 setToast({ message: t("settings.operationPipeline.templateDeleteError"), type: "error" })
 }
 }

 const patchDocTypeRow = async (
 id: string,
 data: {
 name?: string
 slug?: string
 sortOrder?: number
 isRequired?: boolean
 isActive?: boolean
 attachTo?: "sale" | "customer"
 }
 ) => {
 try {
 await apiService.updateSaleCustomerDocumentType(id, data)
 setToast({ message: t("settings.operationPipeline.onboardingDocTypeSaved"), type: "success" })
 void loadSaleOnboarding()
 } catch {
 setToast({ message: t("settings.operationPipeline.onboardingDocTypeSaveError"), type: "error" })
 }
 }

 const addDocType = async () => {
 const name = newDocName.trim()
 const slug = newDocSlug.trim()
 if (!name || !slug) {
 setToast({ message: t("settings.operationPipeline.onboardingDocTypeInvalid"), type: "error" })
 return
 }
 try {
 await apiService.createSaleCustomerDocumentType({
 name,
 slug,
 sortOrder: newDocSort,
 attachTo: newDocAttach,
 })
 setNewDocName("")
 setNewDocSlug("")
 setNewDocSort(0)
 setToast({ message: t("settings.operationPipeline.onboardingDocTypeCreated"), type: "success" })
 void loadSaleOnboarding()
 } catch {
 setToast({ message: t("settings.operationPipeline.onboardingDocTypeCreateError"), type: "error" })
 }
 }

 const confirmDeleteDocType = async () => {
 if (!docTypeToDelete) return
 try {
 await apiService.deleteSaleCustomerDocumentType(docTypeToDelete.id)
 setDocTypeToDelete(null)
 setToast({ message: t("settings.operationPipeline.onboardingDocTypeDeleted"), type: "success" })
 void loadSaleOnboarding()
 } catch {
 setToast({ message: t("settings.operationPipeline.onboardingDocTypeDeleteError"), type: "error" })
 }
 }

 const patchContractTplRow = async (
 id: string,
 data: {
 name?: string
 sortOrder?: number
 isRequired?: boolean
 isActive?: boolean
 referenceFileUrl?: string | null
 referenceTempFilename?: string
 },
 ) => {
 try {
 await apiService.updateSaleContractTemplate(id, data)
 setToast({ message: t("settings.operationPipeline.onboardingContractSaved"), type: "success" })
 void loadSaleOnboarding()
 } catch {
 setToast({ message: t("settings.operationPipeline.onboardingContractSaveError"), type: "error" })
 }
 }

 const addContractTpl = async () => {
 const name = newTplName.trim()
 if (!name) {
 setToast({ message: t("settings.operationPipeline.onboardingContractInvalid"), type: "error" })
 return
 }
 try {
 await apiService.createSaleContractTemplate({
 name,
 sortOrder: newTplSort,
 referenceFileUrl: newTplUrl.trim() || undefined,
 referenceTempFilename: newTplTempFilename.trim() || undefined,
 })
 setNewTplName("")
 setNewTplUrl("")
 setNewTplTempFilename("")
 setNewTplRefLabel("")
 setNewTplSort(0)
 setToast({ message: t("settings.operationPipeline.onboardingContractCreated"), type: "success" })
 void loadSaleOnboarding()
 } catch {
 setToast({ message: t("settings.operationPipeline.onboardingContractCreateError"), type: "error" })
 }
 }

 const confirmDeleteContractTpl = async () => {
 if (!contractTplToDelete) return
 try {
 await apiService.deleteSaleContractTemplate(contractTplToDelete.id)
 setContractTplToDelete(null)
 setToast({ message: t("settings.operationPipeline.onboardingContractDeleted"), type: "success" })
 void loadSaleOnboarding()
 } catch {
 setToast({ message: t("settings.operationPipeline.onboardingContractDeleteError"), type: "error" })
 }
 }

 const uploadContractReferenceToTemp = async (file: File) => {
 const fd = new FormData()
 fd.append("file", file)
 return apiService.uploadSaleContractTemplateReference(fd)
 }

 const handleExistingContractRefUpload = async (templateId: string, file: File) => {
 setContractRefUploadingId(templateId)
 try {
 const { filename } = await uploadContractReferenceToTemp(file)
 await apiService.updateSaleContractTemplate(templateId, { referenceTempFilename: filename })
 setToast({ message: t("settings.operationPipeline.onboardingContractSaved"), type: "success" })
 void loadSaleOnboarding()
 } catch {
 setToast({ message: t("settings.operationPipeline.onboardingContractRefUploadError"), type: "error" })
 } finally {
 setContractRefUploadingId(null)
 }
 }

 const handleNewContractRefUpload = async (file: File) => {
 setContractRefUploadingId(NEW_CONTRACT_UPLOAD_KEY)
 try {
 const { filename } = await uploadContractReferenceToTemp(file)
 setNewTplTempFilename(filename)
 setNewTplRefLabel(file.name)
 } catch {
 setToast({ message: t("settings.operationPipeline.onboardingContractRefUploadError"), type: "error" })
 } finally {
 setContractRefUploadingId(null)
 }
 }

 return (
 <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
 {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
 <div>
 <h1 className="text-2xl font-bold text-foreground">{t("settings.operationPipeline.pageTitle")}</h1>
 <p className="text-sm text-muted-foreground mt-1">{t("settings.operationPipeline.pageSubtitle")}</p>
 </div>

 <Tabs value={tab} onValueChange={(v) => setTab(v as "kanban" | "checklists" | "saleOnboarding")}>
 <TabsList className="grid w-full max-w-md grid-cols-1">
 <TabsTrigger value="saleOnboarding" className="gap-2">
 <FileStack className="h-4 w-4" />
 {t("settings.operationPipeline.tabSaleOnboarding")}
 </TabsTrigger>
 </TabsList>

 <TabsContent value="kanban" className="mt-6 space-y-6">
 <Card>
 <CardHeader>
 <CardTitle className="text-base">{t("settings.operationPipeline.kanbanTitle")}</CardTitle>
 </CardHeader>
 <CardContent className="space-y-4">
 <div className="flex flex-col gap-2 max-w-xs">
 <Label>{t("settings.operationPipeline.boardStage")}</Label>
 <select
 className="h-10 rounded-md border border-input bg-background px-3 text-sm"
 value={kanbanStage}
 onChange={(e) => setKanbanStage(e.target.value as SaleOperationalStage)}
 >
 {KANBAN_STAGES.map((s) => (
 <option key={s} value={s}>
 {t(`operation.stages.${s}`)}
 </option>
 ))}
 </select>
 </div>

 <p className="text-xs text-muted-foreground">{t("settings.operationPipeline.edgeColumnHelp")}</p>
 <div className="space-y-3">
 {columns.map((col, idx) => {
 const n = columns.length
 const isEdge = idx === 0 || idx === n - 1
 const canMoveUp = idx > 1
 const canMoveDown = n >= 2 && idx < n - 2
 return (
 <div key={col.id} className="flex flex-col gap-3 rounded-lg border border-border p-3">
 <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
 <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
 {isEdge ? (
 <div className="space-y-1 sm:max-w-xs">
 <p className="text-sm font-semibold text-foreground">{col.title}</p>
 <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
 {t("settings.operationPipeline.edgeColumnBadge")}
 </span>
 </div>
 ) : (
 <ModernInput
 value={col.title}
 onChange={(e) => {
 const v = e.target.value
 setColumns((prev) => prev.map((c) => (c.id === col.id ? { ...c, title: v } : c)))
 }}
 onBlur={() => {
 const current = columns.find((c) => c.id === col.id)
 const title = current?.title?.trim()
 if (title) void patchColumn(col.id, { title })
 }}
 className="sm:max-w-xs"
 />
 )}
 <div className="flex items-center gap-2">
 <Switch
 checked={col.isActive}
 disabled={isEdge}
 onCheckedChange={(checked) => {
 void patchColumn(col.id, { isActive: checked })
 }}
 />
 <span className="text-sm text-muted-foreground">{t("settings.operationPipeline.active")}</span>
 </div>
 <div className="flex items-center gap-2">
 <Switch
 checked={col.isHandoffColumn}
 disabled={isEdge}
 onCheckedChange={(checked) => {
 void patchColumn(col.id, { isHandoffColumn: checked })
 }}
 />
 <span className="text-sm text-muted-foreground">{t("settings.operationPipeline.handoff")}</span>
 </div>
                  {kanbanStage === "preparation" ? (
                   <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                     {t("settings.operationPipeline.columnResponsible")}
                    </span>
                    <select
                     className="h-9 rounded-md border border-input bg-background px-2 text-sm min-w-[220px]"
                     value={columnResponsibleDrafts[col.id] ?? ""}
                     disabled={isEdge}
                     onChange={(e) =>
                      setColumnResponsibleDrafts((prev) => ({
                       ...prev,
                       [col.id]: e.target.value,
                      }))
                     }
                     onBlur={() => {
                      if (isEdge) return
                      const value = columnResponsibleDrafts[col.id] ?? ""
                      void patchColumn(col.id, { responsibleUserId: value || null })
                     }}
                    >
                     <option value="">{t("settings.operationPipeline.columnResponsibleEmpty")}</option>
                     {preparationAssignees.map((user) => (
                      <option key={user.id} value={user.id}>
                       {user.name}
                      </option>
                     ))}
                    </select>
                   </div>
                  ) : null}
 </div>
 <div className="flex items-center gap-1 shrink-0">
 <ModernButton
 type="button"
 variant="outline"
 size="sm"
 disabled={!canMoveUp}
 onClick={() => void moveColumn(idx, -1)}
 aria-label="up"
 >
 <ChevronUp className="h-4 w-4" />
 </ModernButton>
 <ModernButton
 type="button"
 variant="outline"
 size="sm"
 disabled={!canMoveDown}
 onClick={() => void moveColumn(idx, 1)}
 aria-label="down"
 >
 <ChevronDown className="h-4 w-4" />
 </ModernButton>
 {!isEdge ? (
 <ModernButton
 type="button"
 variant="outline"
 size="sm"
 className="text-destructive"
 onClick={() => setColumnToDelete(col)}
 >
 <Trash2 className="h-4 w-4" />
 </ModernButton>
 ) : null}
 </div>
 </div>
 {!isEdge ? (
 <div className="border-t border-border pt-3 space-y-3">
 <Label className="text-xs">{t("settings.operationPipeline.columnChecklistTitle")}</Label>
 <p className="text-[11px] text-muted-foreground leading-snug">
  {t("settings.operationPipeline.columnChecklistHelp")}
 </p>
 <div className="rounded-lg border border-border/80 bg-muted/15 p-3 space-y-2">
  {(columnChecklistDrafts[col.id] ?? [""]).map((line, lineIdx) => (
   <div key={`${col.id}-line-${lineIdx}`} className="flex items-start gap-2">
    <Checkbox
     disabled
     checked={false}
     className="mt-2 shrink-0 opacity-40 pointer-events-none"
     aria-hidden
    />
    <ModernInput
     value={line}
     onChange={(e) => updateColumnChecklistLine(col.id, lineIdx, e.target.value)}
     placeholder={t("settings.operationPipeline.checklistItemPlaceholder")}
     className="flex-1 text-sm min-h-9"
    />
    <ModernButton
     type="button"
     variant="outline"
     size="sm"
     className="shrink-0 h-9 px-2 text-muted-foreground hover:text-destructive"
     aria-label={t("settings.operationPipeline.removeChecklistItem")}
     onClick={() => removeColumnChecklistLine(col.id, lineIdx)}
    >
     <Trash2 className="h-4 w-4" />
    </ModernButton>
   </div>
  ))}
  <ModernButton
   type="button"
   variant="outline"
   size="sm"
   className="w-full sm:w-auto"
   onClick={() => addColumnChecklistLine(col.id)}
  >
   <Plus className="h-4 w-4 mr-2" />
   {t("settings.operationPipeline.addChecklistItem")}
  </ModernButton>
 </div>
 <ModernButton
 type="button"
 size="sm"
 variant="secondary"
 disabled={checklistSavingId === col.id}
 onClick={() => void saveColumnChecklistLines(col.id)}
 >
 {t("settings.operationPipeline.saveColumnChecklist")}
 </ModernButton>
 </div>
 ) : null}
 </div>
 )
 })}
 </div>

 <div className="flex flex-col gap-2 sm:flex-row sm:items-end border-t border-border pt-4">
 <div className="flex-1">
 <Label>{t("settings.operationPipeline.newColumnTitle")}</Label>
 <ModernInput
 value={newColTitle}
 onChange={(e) => setNewColTitle(e.target.value)}
 placeholder={t("settings.operationPipeline.newColumnPlaceholder")}
 className="mt-1"
 />
 </div>
 <ModernButton type="button" onClick={() => void addColumn()} className="shrink-0">
 <Plus className="h-4 w-4 mr-2" />
 {t("settings.operationPipeline.addColumn")}
 </ModernButton>
 </div>
 </CardContent>
 </Card>
 </TabsContent>

 <TabsContent value="checklists" className="mt-6 space-y-6">
 <Card>
 <CardHeader>
 <CardTitle className="text-base">{t("settings.operationPipeline.checklistsTitle")}</CardTitle>
 </CardHeader>
 <CardContent className="space-y-6">
 <div className="flex flex-col gap-2 max-w-xs">
 <Label>{t("settings.operationPipeline.filterStage")}</Label>
 <select
 className="h-10 rounded-md border border-input bg-background px-3 text-sm"
 value={checklistStageFilter}
 onChange={(e) => setChecklistStageFilter(e.target.value)}
 >
 <option value="">{t("settings.operationPipeline.allStages")}</option>
 {CHECKLIST_STAGES.map((s) => (
 <option key={s} value={s}>
 {t(`operation.stages.${s}`)}
 </option>
 ))}
 </select>
 </div>

 <div className="space-y-3">
 {templates.map((tpl) => (
 <div key={tpl.id} className="rounded-lg border border-border p-3 space-y-2">
  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
   <button
    type="button"
    className="flex flex-1 items-start gap-2 text-left min-w-0"
    onClick={() => setExpandedTplId((id) => (id === tpl.id ? null : tpl.id))}
   >
    <ChevronDown
     className={cn(
      "h-4 w-4 mt-0.5 shrink-0 text-muted-foreground transition-transform",
      expandedTplId === tpl.id ? "rotate-180" : "rotate-0"
     )}
    />
    <div className="min-w-0">
     <p className="font-medium">{tpl.name}</p>
     <p className="text-xs text-muted-foreground">
      {t(`operation.stages.${tpl.operationalStage}` as const)} · {tpl.items.length}{" "}
      {t("settings.operationPipeline.items")}
     </p>
    </div>
   </button>
   <ModernButton
    type="button"
    variant="outline"
    size="sm"
    className="text-destructive shrink-0"
    onClick={(e) => {
     e.stopPropagation()
     setTplToDelete(tpl)
    }}
   >
    <Trash2 className="h-4 w-4 mr-1" />
    {t("actions.delete")}
   </ModernButton>
  </div>
  {expandedTplId === tpl.id ? (
   <div className="rounded-md border border-border/60 bg-muted/10 p-3 space-y-2 ml-6">
    {tpl.items.length === 0 ? (
     <p className="text-xs text-muted-foreground">{t("settings.operationPipeline.templateItemsEmpty")}</p>
    ) : (
     tpl.items.map((it) => (
      <div key={it.id} className="flex items-start gap-2">
       <Checkbox disabled checked={false} className="mt-0.5 shrink-0 opacity-40 pointer-events-none" aria-hidden />
       <span className="text-sm leading-snug">{it.title}</span>
      </div>
     ))
    )}
   </div>
  ) : null}
 </div>
 ))}
 {templates.length === 0 && (
 <p className="text-sm text-muted-foreground">{t("settings.operationPipeline.noTemplates")}</p>
 )}
 </div>

 <div className="border-t border-border pt-4 space-y-3">
 <p className="font-medium text-sm">{t("settings.operationPipeline.newTemplate")}</p>
 <div className="grid gap-3 sm:grid-cols-2">
 <div>
 <Label>{t("common.name")}</Label>
 <ModernInput
 value={tplName}
 onChange={(e) => setTplName(e.target.value)}
 className="mt-1"
 />
 </div>
 <div>
 <Label>{t("settings.operationPipeline.templateStage")}</Label>
 <select
 className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
 value={tplStage}
 onChange={(e) => setTplStage(e.target.value as SaleOperationalStage)}
 >
 {CHECKLIST_STAGES.map((s) => (
 <option key={s} value={s}>
 {t(`operation.stages.${s}`)}
 </option>
 ))}
 </select>
 </div>
 </div>
 <div className="space-y-2">
 <Label>{t("settings.operationPipeline.itemsLines")}</Label>
 <div className="rounded-lg border border-border/80 bg-muted/15 p-3 space-y-2">
  {tplItemLines.map((line, lineIdx) => (
   <div key={`new-tpl-${lineIdx}`} className="flex items-start gap-2">
    <Checkbox
     disabled
     checked={false}
     className="mt-2 shrink-0 opacity-40 pointer-events-none"
     aria-hidden
    />
    <ModernInput
     value={line}
     onChange={(e) => updateNewTplLine(lineIdx, e.target.value)}
     placeholder={t("settings.operationPipeline.checklistItemPlaceholder")}
     className="flex-1 text-sm min-h-9"
    />
    <ModernButton
     type="button"
     variant="outline"
     size="sm"
     className="shrink-0 h-9 px-2 text-muted-foreground hover:text-destructive"
     aria-label={t("settings.operationPipeline.removeChecklistItem")}
     onClick={() => removeNewTplLine(lineIdx)}
    >
     <Trash2 className="h-4 w-4" />
    </ModernButton>
   </div>
  ))}
  <ModernButton type="button" variant="outline" size="sm" className="w-full sm:w-auto" onClick={addNewTplLine}>
   <Plus className="h-4 w-4 mr-2" />
   {t("settings.operationPipeline.addChecklistItem")}
  </ModernButton>
 </div>
 </div>
 <ModernButton type="button" onClick={() => void createTemplate()}>
 <Plus className="h-4 w-4 mr-2" />
 {t("settings.operationPipeline.createTemplate")}
 </ModernButton>
 </div>
 </CardContent>
 </Card>
 </TabsContent>

 <TabsContent value="saleOnboarding" className="mt-6 space-y-6">
 <p className="text-sm text-muted-foreground">{t("settings.operationPipeline.saleOnboardingIntro")}</p>
 <Card>
 <CardHeader>
 <CardTitle className="text-base">{t("settings.operationPipeline.onboardingDocTypesTitle")}</CardTitle>
 </CardHeader>
 <CardContent className="space-y-4">
 <div className="space-y-3">
 {docTypes.map((row) => (
 <div
 key={row.id}
 className="flex flex-col gap-3 rounded-lg border border-border p-3 sm:flex-row sm:items-center sm:justify-between"
 >
 <div>
 <p className="font-medium">{row.name}</p>
 <p className="text-xs text-muted-foreground font-mono">
 {row.slug} · {row.attachTo}
 </p>
 </div>
 <div className="flex flex-wrap items-center gap-3">
 <div className="flex items-center gap-2">
 <Switch
 checked={row.isActive}
 onCheckedChange={(checked) => void patchDocTypeRow(row.id, { isActive: checked })}
 />
 <span className="text-sm text-muted-foreground">{t("settings.operationPipeline.active")}</span>
 </div>
 <div className="flex items-center gap-2">
 <Switch
 checked={row.isRequired}
 onCheckedChange={(checked) => void patchDocTypeRow(row.id, { isRequired: checked })}
 />
 <span className="text-sm text-muted-foreground">{t("settings.operationPipeline.onboardingRequired")}</span>
 </div>
 <ModernButton
 type="button"
 variant="outline"
 size="sm"
 className="text-destructive"
 onClick={() => setDocTypeToDelete(row)}
 >
 <Trash2 className="h-4 w-4" />
 </ModernButton>
 </div>
 </div>
 ))}
 {docTypes.length === 0 && (
 <p className="text-sm text-muted-foreground">{t("settings.operationPipeline.onboardingDocTypesEmpty")}</p>
 )}
 </div>
 <div className="border-t border-border pt-4 space-y-3">
 <p className="font-medium text-sm">{t("settings.operationPipeline.onboardingNewDocType")}</p>
 <div className="grid gap-3 sm:grid-cols-2">
 <div>
 <Label>{t("common.name")}</Label>
 <ModernInput value={newDocName} onChange={(e) => setNewDocName(e.target.value)} className="mt-1" />
 </div>
 <div>
 <Label>{t("settings.operationPipeline.onboardingSlug")}</Label>
 <ModernInput value={newDocSlug} onChange={(e) => setNewDocSlug(e.target.value)} className="mt-1" />
 </div>
 <div>
 <Label>{t("settings.operationPipeline.onboardingAttach")}</Label>
 <select
 className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
 value={newDocAttach}
 onChange={(e) => setNewDocAttach(e.target.value as "sale" | "customer")}
 >
 <option value="sale">{t("settings.operationPipeline.onboardingAttachSale")}</option>
 <option value="customer">{t("settings.operationPipeline.onboardingAttachCustomer")}</option>
 </select>
 </div>
 <div>
 <Label>{t("settings.operationPipeline.onboardingSort")}</Label>
 <ModernInput
 type="number"
 value={newDocSort}
 onChange={(e) => setNewDocSort(Number(e.target.value) || 0)}
 className="mt-1"
 />
 </div>
 </div>
 <ModernButton type="button" onClick={() => void addDocType()}>
 <Plus className="h-4 w-4 mr-2" />
 {t("settings.operationPipeline.onboardingAddDocType")}
 </ModernButton>
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <CardTitle className="text-base">{t("settings.operationPipeline.onboardingContractTemplatesTitle")}</CardTitle>
 </CardHeader>
 <CardContent className="space-y-4">
 <div className="space-y-3">
 {contractTemplates.map((row) => (
 <div
 key={row.id}
 className="flex flex-col gap-3 rounded-lg border border-border p-3 sm:flex-row sm:items-start sm:justify-between"
 >
 <div className="flex-1 space-y-2 min-w-0">
 <p className="font-medium">{row.name}</p>
 <div className="flex flex-wrap items-center gap-2">
 <input
 type="file"
 id={`contract-ref-${row.id}`}
 className="sr-only"
 accept={CONTRACT_REFERENCE_ACCEPT}
 onChange={(e) => {
 const f = e.target.files?.[0]
 e.target.value = ""
 if (f) void handleExistingContractRefUpload(row.id, f)
 }}
 />
 <ModernButton
 type="button"
 variant="outline"
 size="sm"
 disabled={contractRefUploadingId === row.id}
 onClick={() => document.getElementById(`contract-ref-${row.id}`)?.click()}
 >
 {contractRefUploadingId === row.id
 ? t("settings.operationPipeline.onboardingContractRefUploading")
 : t("settings.operationPipeline.onboardingContractRefUpload")}
 </ModernButton>
 {row.referenceFileUrl ? (
 <ModernButton
 type="button"
 variant="outline"
 size="sm"
 onClick={() => window.open(row.referenceFileUrl as string, "_blank", "noopener,noreferrer")}
 >
 {t("settings.operationPipeline.onboardingContractRefOpen")}
 </ModernButton>
 ) : null}
 </div>
 <ModernInput
 value={row.referenceFileUrl ?? ""}
 onChange={(e) => {
 const v = e.target.value
 setContractTemplates((prev) => prev.map((x) => (x.id === row.id ? { ...x, referenceFileUrl: v } : x)))
 }}
 onBlur={() => {
 const current = contractTemplates.find((x) => x.id === row.id)
 void patchContractTplRow(row.id, {
 referenceFileUrl: current?.referenceFileUrl?.trim() || null,
 })
 }}
 placeholder={t("settings.operationPipeline.onboardingRefUrlPlaceholder")}
 className="text-sm"
 />
 </div>
 <div className="flex flex-wrap items-center gap-3">
 <div className="flex items-center gap-2">
 <Switch
 checked={row.isActive}
 onCheckedChange={(checked) => void patchContractTplRow(row.id, { isActive: checked })}
 />
 <span className="text-sm text-muted-foreground">{t("settings.operationPipeline.active")}</span>
 </div>
 <div className="flex items-center gap-2">
 <Switch
 checked={row.isRequired}
 onCheckedChange={(checked) => void patchContractTplRow(row.id, { isRequired: checked })}
 />
 <span className="text-sm text-muted-foreground">{t("settings.operationPipeline.onboardingRequired")}</span>
 </div>
 <ModernButton
 type="button"
 variant="outline"
 size="sm"
 className="text-destructive"
 onClick={() => setContractTplToDelete(row)}
 >
 <Trash2 className="h-4 w-4" />
 </ModernButton>
 </div>
 </div>
 ))}
 {contractTemplates.length === 0 && (
 <p className="text-sm text-muted-foreground">{t("settings.operationPipeline.onboardingContractsEmpty")}</p>
 )}
 </div>
 <div className="border-t border-border pt-4 space-y-3">
 <p className="font-medium text-sm">{t("settings.operationPipeline.onboardingNewContract")}</p>
 <div className="grid gap-3 sm:grid-cols-2">
 <div>
 <Label>{t("common.name")}</Label>
 <ModernInput value={newTplName} onChange={(e) => setNewTplName(e.target.value)} className="mt-1" />
 </div>
 <div>
 <Label>{t("settings.operationPipeline.onboardingSort")}</Label>
 <ModernInput
 type="number"
 value={newTplSort}
 onChange={(e) => setNewTplSort(Number(e.target.value) || 0)}
 className="mt-1"
 />
 </div>
 <div className="sm:col-span-2 space-y-2">
 <Label>{t("settings.operationPipeline.onboardingContractRefUpload")}</Label>
 <div className="flex flex-wrap items-center gap-2">
 <input
 type="file"
 id="contract-ref-new"
 className="sr-only"
 accept={CONTRACT_REFERENCE_ACCEPT}
 onChange={(e) => {
 const f = e.target.files?.[0]
 e.target.value = ""
 if (f) void handleNewContractRefUpload(f)
 }}
 />
 <ModernButton
 type="button"
 variant="outline"
 size="sm"
 disabled={contractRefUploadingId === NEW_CONTRACT_UPLOAD_KEY}
 onClick={() => document.getElementById("contract-ref-new")?.click()}
 >
 {contractRefUploadingId === NEW_CONTRACT_UPLOAD_KEY
 ? t("settings.operationPipeline.onboardingContractRefUploading")
 : t("settings.operationPipeline.onboardingContractRefUpload")}
 </ModernButton>
 {newTplRefLabel ? (
 <span className="text-xs text-muted-foreground">{newTplRefLabel}</span>
 ) : null}
 </div>
 {newTplTempFilename ? (
 <p className="text-xs text-muted-foreground">{t("settings.operationPipeline.onboardingContractRefPending")}</p>
 ) : null}
 </div>
 <div className="sm:col-span-2">
 <Label>{t("settings.operationPipeline.onboardingRefUrlPlaceholder")}</Label>
 <ModernInput value={newTplUrl} onChange={(e) => setNewTplUrl(e.target.value)} className="mt-1" />
 </div>
 </div>
 <ModernButton type="button" onClick={() => void addContractTpl()}>
 <Plus className="h-4 w-4 mr-2" />
 {t("settings.operationPipeline.onboardingAddContract")}
 </ModernButton>
 </div>
 </CardContent>
 </Card>
 </TabsContent>
 </Tabs>

 <ConfirmDialog
 isOpen={!!columnToDelete}
 onClose={() => setColumnToDelete(null)}
 title={t("settings.operationPipeline.confirmDeleteColumn")}
 description={t("settings.operationPipeline.confirmDeleteColumnHint")}
 confirmText={t("actions.delete")}
 cancelText={t("actions.cancel")}
 type="danger"
 onConfirm={() => void confirmDeleteColumn()}
 />

 <ConfirmDialog
 isOpen={!!tplToDelete}
 onClose={() => setTplToDelete(null)}
 title={t("settings.operationPipeline.confirmDeleteTemplate")}
 description={t("settings.operationPipeline.confirmDeleteTemplateHint")}
 confirmText={t("actions.delete")}
 cancelText={t("actions.cancel")}
 type="danger"
 onConfirm={() => void confirmDeleteTemplate()}
 />

 <ConfirmDialog
 isOpen={!!docTypeToDelete}
 onClose={() => setDocTypeToDelete(null)}
 title={t("settings.operationPipeline.confirmDeleteOnboardingDocType")}
 description={t("settings.operationPipeline.confirmDeleteOnboardingDocTypeHint")}
 confirmText={t("actions.delete")}
 cancelText={t("actions.cancel")}
 type="danger"
 onConfirm={() => void confirmDeleteDocType()}
 />

 <ConfirmDialog
 isOpen={!!contractTplToDelete}
 onClose={() => setContractTplToDelete(null)}
 title={t("settings.operationPipeline.confirmDeleteOnboardingContract")}
 description={t("settings.operationPipeline.confirmDeleteOnboardingContractHint")}
 confirmText={t("actions.delete")}
 cancelText={t("actions.cancel")}
 type="danger"
 onConfirm={() => void confirmDeleteContractTpl()}
 />
 </div>
 )
}
